import { useEffect, useRef, useState } from "react";
import type { Key } from "react";
import { useApp } from "../context/AppContext.tsx";
import { type DateFormatOption, formatDate } from "../../shared/date-format.ts";
import type { EnrichedBookmark } from "../../shared/types.ts";
import { useMarkAsRead } from "../hooks/useMarkAsRead.ts";
import { toast } from "sonner";
import { Tag } from "./Tag.tsx";

function ReadingListCard(
  { bookmark, dateFormat, onMarkAsRead }: {
    key?: Key | null;
    bookmark: EnrichedBookmark;
    dateFormat?: DateFormatOption;
    onMarkAsRead: (bookmark: EnrichedBookmark) => void;
  },
) {
  // Extract domain from URL
  const domain = (() => {
    try {
      return new URL(bookmark.subject).hostname.replace("www.", "");
    } catch {
      return bookmark.subject;
    }
  })();

  const formattedDate = formatDate(bookmark.createdAt, dateFormat);

  const handleClick = () => {
    globalThis.open(bookmark.subject, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      onClick={handleClick}
      className="relative group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-shadow hover:shadow-md"
    >
      <button
        type="button"
        title="Mark as read"
        onClick={(e) => {
          e.stopPropagation();
          onMarkAsRead(bookmark);
        }}
        className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/90 shadow-sm border border-gray-200 text-green-600 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-green-50"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </button>
      {bookmark.image && (
        <div className="aspect-[2/1] bg-gray-100 overflow-hidden max-h-40">
          <img
            src={bookmark.image}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).parentElement!.style.display =
                "none";
            }}
          />
        </div>
      )}
      <div className="p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-1.5 line-clamp-2">
          {bookmark.title || domain}
        </h3>
        {bookmark.description && (
          <p className="text-gray-600 text-sm mb-3 line-clamp-3">
            {bookmark.description}
          </p>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {bookmark.favicon && (
            <img
              src={bookmark.favicon}
              alt=""
              className="w-4 h-4"
              loading="lazy"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <span>{domain}</span>
          <span className="text-gray-300">|</span>
          <span>{formattedDate}</span>
        </div>
      </div>
    </article>
  );
}

function ReadingListEmpty({ tagName }: { tagName: string }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="text-6xl mb-4">📚</div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          No articles in your reading list
        </h3>
        <p className="text-gray-600 mb-4">
          Bookmarks tagged with "{tagName}" will appear here. Add this tag to
          any bookmark to save it for later reading.
        </p>
        <a
          href="/settings"
          className="inline-block px-4 py-2 text-sm font-medium rounded-lg transition-colors"
          style={{
            backgroundColor: "rgba(230, 100, 86, 0.1)",
            color: "var(--coral)",
          }}
        >
          Change tag in Settings
        </a>
      </div>
    </div>
  );
}

function ReadingListTagSidebar() {
  const {
    readingListTags,
    readingListSelectedTags,
    toggleReadingListTag,
    clearReadingListFilters,
    preferences,
  } = useApp();

  if (readingListTags.length === 0) {
    return null;
  }

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200 flex-shrink-0">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Filter by Tag</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            {readingListTags.map((tag) => {
              const isSelected = readingListSelectedTags.has(tag);
              const isReadingListTag = tag === preferences.readingListTag;
              return (
                <button
                  type="button"
                  key={tag}
                  onClick={() => toggleReadingListTag(tag)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                    isSelected
                      ? "coral-selected"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {isSelected && (
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                  <span className="truncate">{tag}</span>
                  {isReadingListTag && (
                    <span
                      className={`ml-auto text-xs ${
                        isSelected ? "text-white/70" : "text-gray-400"
                      }`}
                    >
                      primary
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {readingListSelectedTags.size > 0 && (
          <div className="p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={clearReadingListFilters}
              className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Clear filters
            </button>
          </div>
        )}
      </aside>

      {/* Mobile horizontal bar */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 overflow-x-auto">
        <div className="flex items-center gap-2">
          {readingListTags.map((tag) => {
            const isSelected = readingListSelectedTags.has(tag);
            return (
              <Tag
                key={tag}
                variant={isSelected ? "selected" : "unselected"}
                onClick={() => toggleReadingListTag(tag)}
                className="flex-shrink-0"
              >
                {tag}
              </Tag>
            );
          })}
          {readingListSelectedTags.size > 0 && (
            <button
              type="button"
              onClick={clearReadingListFilters}
              className="flex-shrink-0 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export function ReadingList() {
  const {
    filteredReadingList,
    readingListBookmarks,
    totalReadingList,
    preferences,
    readingListSearchQuery,
    setReadingListSearchQuery,
  } = useApp();
  const dateFormat = preferences.dateFormat as DateFormatOption;
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { markAsRead, undo, pending } = useMarkAsRead();
  const prevPendingRef = useRef<string | null>(null);

  useEffect(() => {
    const pendingUri = pending?.bookmark.uri ?? null;
    if (pendingUri && pendingUri !== prevPendingRef.current) {
      toast(`"${pending!.bookmark.title || "Bookmark"}" marked as read`, {
        action: { label: "Undo", onClick: undo },
        duration: 5000,
      });
    }
    prevPendingRef.current = pendingUri;
  }, [pending, undo]);

  // If there are no reading list bookmarks at all, show empty state
  if (readingListBookmarks.length === 0) {
    return (
      <div className="flex flex-col md:flex-row flex-1 md:overflow-hidden">
        <ReadingListEmpty tagName={preferences.readingListTag} />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row flex-1 md:overflow-hidden">
      <ReadingListTagSidebar />
      <main className="flex-1 px-4 py-8 md:overflow-y-auto">
        <div className="max-w-3xl mx-auto">
          {/* Desktop header with search */}
          <div className="hidden md:block mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                Reading List
              </h2>
              <span className="text-sm text-gray-500">
                {readingListSearchQuery.trim()
                  ? `${filteredReadingList.length} of ${totalReadingList}`
                  : filteredReadingList.length}{" "}
                article{filteredReadingList.length !== 1 ? "s" : ""}
              </span>
            </div>
            <input
              type="text"
              placeholder="Search reading list..."
              value={readingListSearchQuery}
              onChange={(e) => setReadingListSearchQuery(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-coral focus:border-transparent outline-none transition"
            />
          </div>

          {/* Mobile header with expandable search */}
          <div className="md:hidden mb-6">
            {mobileSearchOpen
              ? (
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Search reading list..."
                    value={readingListSearchQuery}
                    onChange={(e) => setReadingListSearchQuery(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-coral focus:border-transparent outline-none"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setMobileSearchOpen(false);
                      setReadingListSearchQuery("");
                    }}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )
              : (
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Reading List
                  </h2>
                  <button
                    type="button"
                    onClick={() => setMobileSearchOpen(true)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              )}
            <p className="text-sm text-gray-500">
              {readingListSearchQuery.trim()
                ? `${filteredReadingList.length} of ${totalReadingList}`
                : filteredReadingList.length}{" "}
              article{filteredReadingList.length !== 1 ? "s" : ""}
            </p>
          </div>
          {filteredReadingList.length === 0
            ? (
              <div className="text-center py-12 text-gray-500">
                No articles match the selected filters.
              </div>
            )
            : (
              <div className="space-y-4">
                {filteredReadingList.map((bookmark) => (
                  <ReadingListCard
                    key={bookmark.uri}
                    bookmark={bookmark}
                    dateFormat={dateFormat}
                    onMarkAsRead={markAsRead}
                  />
                ))}
              </div>
            )}
        </div>
      </main>
    </div>
  );
}
